// React
import React, { useState, useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from "react-i18next";

// Global Context
import { GlobalContext } from '../../../contexts/Global';

// Leaflet
import L from "leaflet";
import { MapContainer, TileLayer, Marker } from 'react-leaflet';

// Kendo
import { Form, Field, FormElement, FieldWrapper } from "@progress/kendo-react-form";
import { Label } from "@progress/kendo-react-labels";
import { Input } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { process } from "@progress/kendo-data-query";

// Components
import Title from '../../../components/Page/Title';
import Loader from '../../../components/Loader/Loader';
import Success from '../../../components/Page/Success';
import { default as YaardField } from '../../../components/Page/Field';
import Editor from '../../../components/Forms/Editor/Editor';

const CleaningList = () => {
    const { t } = useTranslation();
    const { cleaningCategory = 'collection_point' } = useParams();
    const [ isLoading, showLoader ] = useState(false);
    const [ response, setResponse ] = useState(null);
    const { user } = useContext(GlobalContext);
    const [ items, setItems ] = useState([]);
    const [ category, setCategory ] = useState(null);

    const initialDataState = {
        filter: undefined,
        sort: [
            {
                field: "id",
                dir: "asc"
            }
        ]
    };
    const [ dataState, setDataState ] = useState(initialDataState);
    const [ dataResult, setDataResult ] = useState(
        process(items, dataState)
    );

    const markerIcon = new L.Icon({
        iconUrl: require("../../../assets/icons/cleaning/glass.svg").default,
        iconSize: [ 12, 14 ],
        iconAnchor: [ 6, 7 ]
    });

    useEffect(()=> {
        fetch(process.env.REACT_APP_API_BASE_URL + "/admin/cleaning/" + cleaningCategory, {
            headers: {
                Authorization: 'Bearer ' + user.access_token
            }
        })
            .then((response) => response.json())
            .then((response) => {
                setItems(response.data.cleaning);
                setCategory(response.data.category);
                setDataResult(process(response.data.cleaning, dataState));
    });
    }, [ user, setItems, cleaningCategory, dataState ])

    const Coordinates = (props) => {
        let coordinates = '-';
        if (props.dataItem.lat && props.dataItem.lon) {
            coordinates = (
                <>
                    Lat: { props.dataItem.lat.toFixed(5) }<br />Lon: { props.dataItem.lon.toFixed(5) }
                </>
            );
        }
        return (
            <td style={{ whiteSpace: 'nowrap' }}>{ coordinates }</td>
        );
    }

    const Photo = (props) => {
        if (props.dataItem.photos && props.dataItem.photos.length > 0) {
            return (
                <td>
                    <img src={ props.dataItem.photos[0].url } alt="" style={{ maxHeight: '100px' }} />
                </td>
            )
        }
        else {
            return (
                <td></td>
            )
        }
    }

    const saveCategory = (data, e) => {
        console.log(data);

        showLoader(true);

        fetch(process.env.REACT_APP_API_BASE_URL + '/admin/cleaning/category/' + category.id, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + user.access_token
            },
            body: JSON.stringify(data)
        })
        .then((response) => response.json())
        .then((response) => {
            showLoader(false);
            setResponse(response);
        })
        .catch((err) => {
        });
    }

    return (
        <>
            <Title>
                <div>Lista { t('cleaning_categories.' + cleaningCategory) } <small>({ items.length })</small></div>
            </Title>
            <div className="section grid-template-two-columns">
                <div className="left k-d-flex k-flex-col">
                    <div className="section">
                        <Form
                            key={ category ? category.name : null }
                            initialValues={ category }
                            onSubmit={ saveCategory }
                            render={(formRenderProps) => (
                                <FormElement>
                                    <div className="k-d-flex k-gap-xl">
                                        <input type="hidden" name="changed" />

                                        <div className="k-flex-grow k-d-flex k-flex-col k-gap-xl">
                                            <div>
                                                <fieldset className="k-form-fieldset">
                                                    <FieldWrapper>
                                                        <Label className="k-form-label">Nome</Label>
                                                        <div className="k-form-field-wrap">
                                                            <Field name="label" component={ Input } />
                                                        </div>
                                                    </FieldWrapper>
                                                </fieldset>
                                            </div>

                                            <div>
                                                <div className="k-form-buttons k-justify-content-between k-flex-wrap">
                                                    <Button type="submit" rounded={ null } size="large" themeColor="primary" disabled={ !formRenderProps.modified }>SALVA</Button>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="k-flex-grow">
                                            <fieldset className="k-form-fieldset">
                                                <FieldWrapper>
                                                    <Label className="k-form-label">Testo</Label>
                                                    <div className="k-form-field-wrap">
                                                        <Field name="text" component={ Editor } />
                                                    </div>
                                                </FieldWrapper>
                                            </fieldset>
                                        </div>

                                    </div>
                                </FormElement>
                            )}
                        />
                    </div>

                    <Grid
                        style={{
                            height: "100%",
                        }}
                        className="k-mt-6"
                        scrollable="scrollable"
                        fixedScroll={ true }
                        sortable={ true }
                        filterable={ false }
                        data={ dataResult }
                        { ...dataState }
                        onDataStateChange={(e) => {
                            setDataState(e.dataState);
                            setDataResult(process(items, e.dataState));
                        }}
                    >
                        <GridColumn field="id" title="Id" width="100px" />
                        <GridColumn field="coordinates" title="Coordinate" cell={ Coordinates } />
                        <GridColumn field="photo" title="Foto" cell={ Photo } width="200px" />
                    </Grid>
                </div>

                <div className="right">
                    <YaardField label="Mappa" className="k-h-full">
                        <MapContainer center={[ 45.3140617, 9.505 ]} zoom={ 14 } scrollWheelZoom={ true } zoomControl={ false }>
                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

                            { items.length > 0 && items.map((item, index) =>
                                <Marker
                                    key={ `marker-${index}` }
                                    position={[ item.lat, item.lon ]}
                                    icon={ markerIcon }
                                >
                                </Marker>
                            )}
                        </MapContainer>
                    </YaardField>
                </div>
            </div>

            <Success enter={ response } exit={ setResponse } />

            { isLoading && (<Loader />) }
        </>
    );
}

export default CleaningList;
